import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { useDestrierTheme, getCardSx } from '../ThemeContext';
import { useDataCache } from '../DataCacheContext';
import { API_BASE } from '../api';
import TimePeriodFilter from './TimePeriodFilter';

const ACCENT = '#8F7BE0';
const TYPE_LABELS = { bullet: 'Bullet', blitz: 'Blitz', rapid: 'Rapid', classical: 'Classical', daily: 'Daily' };

function DeltaArrow({ value, size = 16 }) {
  if (value === 0) return null;
  const up = value > 0;
  const color = up ? '#00A5C4' : '#E4446E';
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: 4 }}>
      {up ? (
        <polygon points="8,2 14,11 2,11" fill={color} />
      ) : (
        <polygon points="8,14 14,5 2,5" fill={color} />
      )}
    </svg>
  );
}


function ChartTooltip({ active, payload, c, byDay }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  // The chart date field is a raw string like "YYYY-MM-DD HH:MM:SS..." — take the first 10 chars
  const iso = d.date ? String(d.date).slice(0, 10) : null;
  const dayStats = byDay && iso ? byDay[iso] : null;
  return (
    <Box sx={{ background: c.bg, border: `1px solid ${c.textMuted}25`, borderRadius: '12px', p: 2, boxShadow: `0 4px 16px ${c.textMuted}15` }}>
      <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', mb: 0.5 }}>{iso || d.date}</Typography>
      <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 700 }}>{d.elo} Elo</Typography>
      {dayStats && dayStats.count > 0 && (
        <Typography sx={{ color: c.textMuted, fontSize: '0.95rem', mt: 0.5 }}>
          {dayStats.count} games · <span style={{ color: '#00A5C4', fontWeight: 600 }}>{dayStats.wins}W</span> · <span style={{ color: '#E4446E', fontWeight: 600 }}>{dayStats.losses}L</span> · {dayStats.draws}D
        </Typography>
      )}
    </Box>
  );
}

export default function ProgressPage() {
  const { c } = useDestrierTheme();
  const cardSx = getCardSx(c);
  const cache = useDataCache();
  const [searchParams] = useSearchParams();
  const username = searchParams.get('username');
  const [period, setPeriod] = useState('all');
  const [eloData, setEloData] = useState(null);
  const [, setStatsData] = useState(null); // fetched for cache warmup; no UI consumes it currently
  const [gamesData, setGamesData] = useState(null);
  const [phase, setPhase] = useState('fetching');
  const [filterType, setFilterType] = useState(null); // global filter driving everything on this page
  const [chartData, setChartData] = useState([]);
  const [heatmapYear, setHeatmapYear] = useState(new Date().getUTCFullYear());
  const [heatmapMode, setHeatmapMode] = useState('volume'); // 'volume' | 'winrate'
  const [heatmapHover, setHeatmapHover] = useState(null); // { cell, x, y } for custom tooltip

  useEffect(() => {
    if (!username) return;
    const ekc = `elo:${username}:${period}`;
    const skc = `stats:${username}:${period}`;
    const hitElo = cache.get(ekc);
    const hitStats = cache.get(skc);
    if (hitElo) setEloData(hitElo);
    if (hitStats) setStatsData(hitStats);
    let eloReady = !!hitElo, statsReady = !!hitStats;
    if (eloReady && statsReady) { setPhase('ready'); return; }
    setPhase('fetching');
    let cancelled = false;
    const checkDone = () => { if (!cancelled && eloReady && statsReady) setPhase('ready'); };
    if (!hitElo) {
      fetch(`${API_BASE}/elo-trajectory?username=${username}&period=${period}`)
        .then(r => r.ok ? r.json() : Promise.reject())
        .then(d => { if (!cancelled) { cache.set(ekc, d); setEloData(d); } eloReady = true; checkDone(); })
        .catch(() => { eloReady = true; checkDone(); });
    }
    if (!hitStats) {
      fetch(`${API_BASE}/stats?username=${username}&period=${period}`)
        .then(r => r.ok ? r.json() : Promise.reject())
        .then(d => { if (!cancelled) { const f = d.filter(s => s.game_type !== 'unknown'); cache.set(skc, f); setStatsData(f); } statsReady = true; checkDone(); })
        .catch(() => { statsReady = true; checkDone(); });
    }
    return () => { cancelled = true; };
  }, [username, period]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch raw games once (all-time) for recent-form, streaks, heatmap, colour split, period compare
  useEffect(() => {
    if (!username) return;
    const ck = `games:${username}:all`;
    const hit = cache.get(ck);
    if (hit) { setGamesData(hit); return; }
    let cancelled = false;
    fetch(`${API_BASE}/games?username=${encodeURIComponent(username)}&period=all`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => { if (!cancelled && Array.isArray(d)) { cache.set(ck, d); setGamesData(d); } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [username]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (eloData && !filterType) {
      const types = Object.entries(eloData.trajectories);
      if (types.length > 0) setFilterType(types.sort((a, b) => b[1].games - a[1].games)[0][0]);
    }
  }, [eloData, filterType]);

  useEffect(() => {
    setChartData([]);
    const t = setTimeout(() => {
      if (eloData && filterType && eloData.trajectories[filterType]) {
        const pts = eloData.trajectories[filterType].points;
        let sampled = pts;
        if (pts.length > 200) {
          const step = Math.ceil(pts.length / 200);
          sampled = pts.filter((_, i) => i % step === 0);
          if (sampled[sampled.length - 1] !== pts[pts.length - 1]) sampled.push(pts[pts.length - 1]);
        }
        setChartData(sampled);
      }
    }, 50);
    return () => clearTimeout(t);
  }, [filterType, eloData]);

  // Derived stats from raw games — respects the global filterType
  const derived = useMemo(() => {
    if (!Array.isArray(gamesData) || gamesData.length === 0) return null;
    const base = gamesData.filter(g => g.game_date_utc && g.outcome);
    const sorted = [...base]
      .filter(g => !filterType || g.game_type === filterType)
      .sort((a, b) => new Date(a.game_date_utc) - new Date(b.game_date_utc));
    if (sorted.length === 0) return null;

    // Recent form — last 20 games (objects, newest last) so hover can show details
    const recent = sorted.slice(-20);

    // Current streak
    let streakType = null, streakLen = 0;
    for (let i = sorted.length - 1; i >= 0; i--) {
      const o = sorted[i].outcome;
      if (streakType === null) { streakType = o; streakLen = 1; }
      else if (o === streakType) streakLen++;
      else break;
    }

    // Longest win / loss streaks (all time, within selected type)
    let longestWin = 0, longestLoss = 0, runW = 0, runL = 0;
    for (const g of sorted) {
      if (g.outcome === 'win') { runW++; runL = 0; if (runW > longestWin) longestWin = runW; }
      else if (g.outcome === 'loss') { runL++; runW = 0; if (runL > longestLoss) longestLoss = runL; }
      else { runW = 0; runL = 0; }
    }

    // Colour split — also compute avg opp elo and elo change by colour
    const whites = sorted.filter(g => g.color === 'white');
    const blacks = sorted.filter(g => g.color === 'black');
    const avg = (list, f) => list.length ? list.reduce((s, x) => s + (f(x) || 0), 0) / list.length : 0;
    const eloMoveByColor = (list) => list.length >= 2 ? (list[list.length - 1].player_elo || 0) - (list[0].player_elo || 0) : 0;
    const whiteWins = whites.filter(g => g.outcome === 'win').length;
    const blackWins = blacks.filter(g => g.outcome === 'win').length;
    const whiteDraws = whites.filter(g => g.outcome === 'draw').length;
    const blackDraws = blacks.filter(g => g.outcome === 'draw').length;

    // Group by day (used by chart tooltip and heatmap). Normalise any input format via Date.
    const byDay = {};
    const toDayKey = (val) => {
      if (!val) return null;
      const d = new Date(val);
      if (isNaN(d.getTime())) {
        const s = String(val).slice(0, 10);
        return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
      }
      return d.toISOString().slice(0, 10);
    };
    for (const g of sorted) {
      const day = toDayKey(g.game_date_utc);
      if (!day) continue;
      if (!byDay[day]) byDay[day] = { count: 0, wins: 0, losses: 0, draws: 0 };
      byDay[day].count++;
      if (g.outcome === 'win') byDay[day].wins++;
      else if (g.outcome === 'loss') byDay[day].losses++;
      else byDay[day].draws++;
    }

    // All years present in the data (for the heatmap year selector)
    const yearsSet = new Set();
    for (const key of Object.keys(byDay)) yearsSet.add(parseInt(key.slice(0, 4), 10));
    const availableYears = [...yearsSet].filter(y => !isNaN(y)).sort((a, b) => b - a);

    // Last-20-games stats (replacing "last 30 days")
    const last20 = sorted.slice(-20);
    const prev20 = sorted.slice(-40, -20);
    const bucket = (list) => ({
      games: list.length,
      winrate: list.length ? list.filter(g => g.outcome === 'win').length / list.length : 0,
      eloChange: list.length >= 2 ? (list[list.length - 1].player_elo || 0) - (list[0].player_elo || 0) : 0,
    });

    return {
      recent, streakType, streakLen, longestWin, longestLoss,
      whiteWR: whites.length ? whiteWins / whites.length : 0,
      whiteGames: whites.length, whiteWins, whiteDraws,
      whiteAvgOppElo: Math.round(avg(whites, g => g.color === 'white' ? g.black_player_elo : g.white_player_elo)),
      whiteEloChange: Math.round(eloMoveByColor(whites)),
      blackWR: blacks.length ? blackWins / blacks.length : 0,
      blackGames: blacks.length, blackWins, blackDraws,
      blackAvgOppElo: Math.round(avg(blacks, g => g.color === 'black' ? g.white_player_elo : g.black_player_elo)),
      blackEloChange: Math.round(eloMoveByColor(blacks)),
      byDay, availableYears,
      last20: bucket(last20), prev20: bucket(prev20),
    };
  }, [gamesData, filterType]);

  // Build the heatmap grid for the selected year — separate memo so changing year doesn't recompute everything
  const heatmap = useMemo(() => {
    if (!derived) return null;
    const { byDay } = derived;
    // Jan 1 of selected year → find the Monday on/before it
    const firstOfYear = new Date(Date.UTC(heatmapYear, 0, 1));
    const startMonday = new Date(firstOfYear);
    startMonday.setUTCDate(startMonday.getUTCDate() - ((startMonday.getUTCDay() + 6) % 7));
    // Iterate week-by-week until we pass Dec 31 of selected year
    const weeks = [];
    const cursor = new Date(startMonday);
    const endOfYear = new Date(Date.UTC(heatmapYear, 11, 31));
    while (cursor <= endOfYear) {
      const days = [];
      for (let d = 0; d < 7; d++) {
        const cell = new Date(cursor);
        cell.setUTCDate(cell.getUTCDate() + d);
        const key = cell.toISOString().slice(0, 10);
        const inYear = cell.getUTCFullYear() === heatmapYear;
        days.push({
          key, inYear,
          count: byDay[key]?.count || 0,
          wins: byDay[key]?.wins || 0,
          losses: byDay[key]?.losses || 0,
          draws: byDay[key]?.draws || 0,
        });
      }
      weeks.push({ weekStart: cursor.toISOString().slice(0, 10), days });
      cursor.setUTCDate(cursor.getUTCDate() + 7);
    }
    const counts = weeks.flatMap(w => w.days.filter(d => d.inYear).map(d => d.count)).filter(n => n > 0);
    const maxDayCount = counts.length ? Math.max(...counts) : 1;
    const totalGames = counts.reduce((s, n) => s + n, 0);
    return { weeks, maxDayCount, totalGames };
  }, [derived, heatmapYear]);

  // Auto-pick the newest available year when data arrives
  useEffect(() => {
    if (derived && derived.availableYears.length > 0 && !derived.availableYears.includes(heatmapYear)) {
      setHeatmapYear(derived.availableYears[0]);
    }
  }, [derived]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!username) {
    return <Box sx={{ minHeight: '100vh', background: c.pageBg || c.bg, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Typography sx={{ color: c.textMuted }}>Please provide a username.</Typography></Box>;
  }

  const activeTrajectory = eloData && filterType ? eloData.trajectories[filterType] : null;
  // Derive available game types from ALL-TIME games data so the filter stays visible regardless of the period filter
  const availableTypes = (() => {
    if (!Array.isArray(gamesData) || gamesData.length === 0) {
      return eloData ? Object.keys(eloData.trajectories).sort((a, b) => (eloData.trajectories[b]?.games || 0) - (eloData.trajectories[a]?.games || 0)) : [];
    }
    const counts = {};
    for (const g of gamesData) {
      const t = g.game_type;
      if (!t || t === 'unknown') continue;
      counts[t] = (counts[t] || 0) + 1;
    }
    return Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  })();

  return (
    <Box sx={{ minHeight: '100vh', background: c.pageBg || c.bg }}>
      {phase === 'fetching' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', border: `3px solid ${c.accent}20`, borderTopColor: c.accent, animation: 'spin 0.8s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
        </Box>
      )}

      {phase === 'ready' && (
        <Box sx={{ px: { xs: 2, md: 3, lg: 4 }, pb: 6, pt: { xs: 9, md: 10 } }}>
          <Box sx={{ position: 'fixed', top: { xs: 56, md: 72 }, left: { xs: 0, md: 0 }, right: { xs: 0, md: '35px' }, display: 'flex', justifyContent: 'center', zIndex: 40, py: 1, pointerEvents: 'none' }}>
            <Box sx={{ pointerEvents: 'auto' }}>
              <TimePeriodFilter value={period} onChange={setPeriod} />
            </Box>
          </Box>

          {/* Page Header */}
          <Box sx={{ mb: 5 }}>
            <Typography sx={{ color: c.textDark, fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.6rem' }, fontWeight: 900, letterSpacing: '-0.03em', mb: 1 }}>
              Progress
            </Typography>
            <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7 }}>
              "Am I getting better?" — the question every chess player asks.
              This page tracks your rating journey, game volume, and win rates across all time controls.
              Use it to spot trends, celebrate milestones, and identify which formats deserve more of your attention.
            </Typography>
          </Box>

          {/* ── Game type filter — Rivals-style pills ── */}
          {availableTypes.length > 1 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
              {availableTypes.map((type) => {
                const active = type === filterType;
                const label = TYPE_LABELS[type] || (type.charAt(0).toUpperCase() + type.slice(1));
                return (
                  <Box
                    key={type}
                    onClick={() => setFilterType(type)}
                    sx={{
                      px: 2, py: 0.7, borderRadius: '999px', cursor: 'pointer',
                      fontSize: '0.92rem', fontWeight: 700,
                      backgroundColor: active ? c.accent : 'transparent',
                      color: active ? '#fff' : c.textMuted,
                      border: `1.5px solid ${active ? c.accent : `${c.textMuted}30`}`,
                      transition: 'all 0.2s',
                      '&:hover': { borderColor: c.accent, color: active ? '#fff' : c.accent },
                    }}
                  >
                    {label}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* ── Rating Overview (for selected type) ── */}
          {activeTrajectory && (
            <Box sx={{ mb: 5 }}>
              <Typography sx={{ color: c.textDark, fontSize: { xs: '1.4rem', md: '1.6rem' }, fontWeight: 800, mb: 0.5 }}>Rating Overview</Typography>
              <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mb: 2 }}>
                Your current standing across {activeTrajectory.games?.toLocaleString()} {TYPE_LABELS[filterType] || filterType} games.
                The net change shows your overall trajectory — positive means you're climbing, negative means it's time to refocus.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
                {(() => {
                  const cur = activeTrajectory.current;
                  const peak = activeTrajectory.peak;
                  const low = activeTrajectory.lowest;
                  // Net Change = peak − lowest (the range the user has swung through)
                  const net = (peak != null && low != null) ? peak - low : 0;
                  const dPeak = cur - peak;
                  const dLow = cur - low;
                  return [
                    { label: 'Current', value: cur, color: c.textDark, showArrow: true, delta: net },
                    { label: 'Peak', value: peak, color: '#00A5C4', sub: dPeak === 0 ? 'at peak' : `${dPeak} from peak` },
                    { label: 'Lowest', value: low, color: ACCENT, sub: `+${dLow} from lowest` },
                    { label: 'Net Change', value: `${net > 0 ? '+' : ''}${net}`, color: net >= 0 ? '#00A5C4' : '#E4446E', sub: 'peak − lowest' },
                  ].map((s) => (
                    <Box key={s.label} sx={{ ...cardSx, p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
                      <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 500, mb: 0.5 }}>{s.label}</Typography>
                      <Typography sx={{ color: s.color, fontSize: { xs: '1.3rem', sm: '1.6rem' }, fontWeight: 800, lineHeight: 1.2 }}>
                        {s.value}{s.showArrow && <DeltaArrow value={s.delta} size={18} />}
                      </Typography>
                      {s.sub && (
                        <Typography sx={{ color: c.textMuted, fontSize: '0.9rem', fontWeight: 500, mt: 0.3 }}>{s.sub}</Typography>
                      )}
                    </Box>
                  ));
                })()}
              </Box>

              {/* Rating chart — sits directly under the 4 tiles */}
              <Box sx={{ ...cardSx, p: { xs: 1.5, sm: 3 }, height: { xs: 320, sm: 400, md: 450 }, mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                  <Typography sx={{ fontSize: '1.05rem', color: c.textMuted, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    Rating Over Time ({TYPE_LABELS[filterType] || filterType})
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#00A5C4' }} />
                      <Typography sx={{ color: c.textMuted, fontSize: '0.9rem', fontWeight: 600 }}>Peak</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#E4446E' }} />
                      <Typography sx={{ color: c.textMuted, fontSize: '0.9rem', fontWeight: 600 }}>Lowest</Typography>
                    </Box>
                  </Box>
                </Box>
                <ResponsiveContainer width="100%" height="90%">
                  <AreaChart data={chartData} margin={{ top: 20, right: 30, bottom: 10, left: 10 }}>
                    <defs>
                      <linearGradient id="eloGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#DE7345" stopOpacity={0.18} />
                        <stop offset="100%" stopColor="#DE7345" stopOpacity={0.02} />
                      </linearGradient>
                      <style>{`
                        @keyframes elo-dot-in { from { opacity: 0; transform: scale(0.6); } to { opacity: 1; transform: scale(1); } }
                        .elo-dot { animation: elo-dot-in 0.35s ease-out both; transform-box: fill-box; transform-origin: center; }
                      `}</style>
                    </defs>
                    <CartesianGrid stroke={`${c.textMuted}12`} strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fill: c.textMuted, fontSize: 14, fontWeight: 500 }} tickLine={false}
                      axisLine={{ stroke: `${c.textMuted}20` }}
                      tickFormatter={(d) => { const iso = String(d).slice(0, 10); const parts = iso.split('-'); return parts.length === 3 ? `${parts[1]}/${parts[0].slice(2)}` : iso; }}
                      interval="preserveStartEnd" minTickGap={60} />
                    <YAxis tick={{ fill: c.textMuted, fontSize: 14, fontWeight: 500 }} tickLine={false}
                      axisLine={{ stroke: `${c.textMuted}20` }} domain={['auto', 'auto']} width={50} />
                    <Tooltip content={<ChartTooltip c={c} byDay={derived?.byDay} />} />
                    {(() => {
                      let peakIdx = -1, lowIdx = -1;
                      if (chartData && chartData.length > 0) {
                        peakIdx = 0; lowIdx = 0;
                        for (let i = 0; i < chartData.length; i++) {
                          if (chartData[i].elo > chartData[peakIdx].elo) peakIdx = i;
                          if (chartData[i].elo < chartData[lowIdx].elo) lowIdx = i;
                        }
                      }
                      const total = chartData ? chartData.length : 1;
                      const AREA_ANIM_MS = 1500; // Recharts Area default animation duration
                      const delayFor = (idx) => (idx / Math.max(total - 1, 1)) * (AREA_ANIM_MS / 1000);
                      const CustomDot = (props) => {
                        const { cx, cy, index } = props;
                        if (cx == null || cy == null) return null;
                        if (index === peakIdx) {
                          const d = delayFor(index);
                          return (
                            <g key={`peak-${index}`} className="elo-dot" style={{ animationDelay: `${d}s` }}>
                              <circle cx={cx} cy={cy} r={8} fill="#00A5C4" stroke={c.bg} strokeWidth={2.5} />
                              <text x={cx} y={cy - 14} textAnchor="middle" fill="#00A5C4" fontSize={12} fontWeight={700}>Peak {chartData[peakIdx].elo}</text>
                            </g>
                          );
                        }
                        if (index === lowIdx) {
                          const d = delayFor(index);
                          return (
                            <g key={`low-${index}`} className="elo-dot" style={{ animationDelay: `${d}s` }}>
                              <circle cx={cx} cy={cy} r={8} fill="#E4446E" stroke={c.bg} strokeWidth={2.5} />
                              <text x={cx} y={cy + 22} textAnchor="middle" fill="#E4446E" fontSize={12} fontWeight={700}>Low {chartData[lowIdx].elo}</text>
                            </g>
                          );
                        }
                        return null;
                      };
                      return (
                        <Area type="monotone" dataKey="elo" stroke="#DE7345"
                          strokeWidth={2} fill="url(#eloGrad)"
                          animationDuration={AREA_ANIM_MS}
                          dot={<CustomDot />}
                          activeDot={{ r: 5, stroke: c.bg, strokeWidth: 2, fill: '#DE7345' }} />
                      );
                    })()}
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          )}

          {/* ── Activity heatmap — GitHub-style ── */}
          {derived && heatmap && heatmap.weeks.length > 0 && (() => {
            const volumeColor = (count) => {
              if (count === 0) return { bg: `${c.textMuted}22`, border: `${c.textMuted}10` };
              const ratio = count / heatmap.maxDayCount;
              if (ratio <= 0.25) return { bg: '#00A5C455', border: '#00A5C470' };
              if (ratio <= 0.5)  return { bg: '#00A5C488', border: '#00A5C4AA' };
              if (ratio <= 0.75) return { bg: '#00A5C4BB', border: '#00A5C4CC' };
              return { bg: '#00A5C4', border: '#00A5C4' };
            };
            // Winrate colour: red → neutral → blue, opacity based on distance from 50%
            const winrateColor = (cell) => {
              if (cell.count === 0) return { bg: `${c.textMuted}22`, border: `${c.textMuted}10` };
              const wr = cell.wins / cell.count;
              if (wr === 0.5) return { bg: `${c.textMuted}55`, border: `${c.textMuted}70` };
              const strength = Math.abs(wr - 0.5) * 2; // 0..1
              const alphaHex = Math.round(0x40 + strength * 0xBF).toString(16).padStart(2, '0'); // 40..FF
              const color = wr > 0.5 ? '#00A5C4' : '#E4446E';
              return { bg: color + alphaHex, border: color + alphaHex };
            };
            const colorFor = (cell) => heatmapMode === 'volume' ? volumeColor(cell.count) : winrateColor(cell);
            const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
            const monthLabelAt = (idx) => {
              const wk = heatmap.weeks[idx];
              if (!wk) return null;
              const firstInYear = wk.days.find(d => d.inYear);
              if (!firstInYear) return null;
              const month = new Date(firstInYear.key + 'T00:00:00Z').getUTCMonth();
              const prevWk = idx > 0 ? heatmap.weeks[idx - 1] : null;
              const prevFirst = prevWk?.days.find(d => d.inYear);
              const prevMonth = prevFirst ? new Date(prevFirst.key + 'T00:00:00Z').getUTCMonth() : -1;
              if (month === prevMonth) return null;
              return new Date(firstInYear.key + 'T00:00:00Z').toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
            };
            const years = derived.availableYears.length ? derived.availableYears : [new Date().getUTCFullYear()];
            return (
              <Box sx={{ mb: 5, position: 'relative' }}>
                <Typography sx={{ color: c.textDark, fontSize: { xs: '1.4rem', md: '1.6rem' }, fontWeight: 800, mb: 0.5 }}>Activity</Typography>
                <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mb: 2 }}>
                  Every square is one day in {heatmapYear} ({heatmap.totalGames.toLocaleString()} games). {heatmapMode === 'volume'
                    ? 'Darker means a heavier session.'
                    : 'Blue means you dominated that day, red means you struggled — intensity scales with how far from 50%.'} Hover any square for the breakdown.
                </Typography>
                {/* Mode toggle — centred above the chart */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 0.3, bgcolor: `${c.textMuted}10`, borderRadius: '10px', p: 0.3 }}>
                    {[{ k: 'volume', l: 'Volume' }, { k: 'winrate', l: 'Win rate' }].map(({ k, l }) => {
                      const active = k === heatmapMode;
                      return (
                        <Box key={k} onClick={() => setHeatmapMode(k)} sx={{
                          px: 2, py: 0.6, borderRadius: '8px', cursor: 'pointer',
                          bgcolor: active ? c.bg : 'transparent',
                          boxShadow: active ? `0 2px 8px ${c.textMuted}15` : 'none',
                          fontWeight: active ? 700 : 500,
                          color: active ? c.textDark : c.textMuted,
                          fontSize: '0.95rem',
                        }}>{l}</Box>
                      );
                    })}
                  </Box>
                </Box>
                <Box
                  data-heatmap-wrap
                  sx={{ ...cardSx, p: 3, position: 'relative' }}
                  onMouseLeave={() => setHeatmapHover(null)}
                >
                  <Typography sx={{ fontSize: '1.05rem', color: c.textMuted, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 2 }}>
                    Activity Calendar ({TYPE_LABELS[filterType] || filterType})
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                    {/* Heatmap grid — weekday labels sit inside each row so they line up perfectly */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Month header row (offset by the weekday label column width) */}
                      <Box sx={{ display: 'flex', gap: '3px', mb: '10px', height: 18 }}>
                        <Box sx={{ minWidth: 42, flexShrink: 0 }} />
                        {heatmap.weeks.map((_, idx) => {
                          const m = monthLabelAt(idx);
                          return (
                            <Box key={idx} sx={{ flex: 1, position: 'relative' }}>
                              {m && <Typography sx={{ color: c.textMuted, fontSize: '0.95rem', fontWeight: 600, position: 'absolute', top: 0, left: 0, whiteSpace: 'nowrap' }}>{m}</Typography>}
                            </Box>
                          );
                        })}
                      </Box>
                      {/* 7 day rows, each row contains its weekday label + cells */}
                      {[0, 1, 2, 3, 4, 5, 6].map((dayIdx) => (
                        <Box key={dayIdx} sx={{ display: 'flex', gap: '3px', mb: '3px', alignItems: 'stretch' }}>
                          <Box sx={{ minWidth: 42, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ color: c.textMuted, fontSize: '0.95rem', fontWeight: 600, lineHeight: 1 }}>{dayLabels[dayIdx]}</Typography>
                          </Box>
                          {heatmap.weeks.map((w, wIdx) => {
                            const cell = w.days[dayIdx];
                            if (!cell.inYear) {
                              return <Box key={wIdx} sx={{ flex: 1, aspectRatio: '1 / 1', opacity: 0 }} />;
                            }
                            const { bg, border } = colorFor(cell);
                            return (
                              <Box
                                key={wIdx}
                                onMouseEnter={(e) => {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  const parent = e.currentTarget.closest('[data-heatmap-wrap]');
                                  if (!parent) return;
                                  const parentRect = parent.getBoundingClientRect();
                                  setHeatmapHover({
                                    cell,
                                    x: rect.left - parentRect.left + rect.width / 2,
                                    y: rect.top - parentRect.top,
                                  });
                                }}
                                sx={{
                                  flex: 1, aspectRatio: '1 / 1',
                                  borderRadius: '3px',
                                  bgcolor: bg, border: `1px solid ${border}`,
                                  transition: 'transform 0.1s, box-shadow 0.1s',
                                  cursor: 'default',
                                  '&:hover': { transform: 'scale(1.3)', boxShadow: `0 0 0 2px ${c.textMuted}40`, zIndex: 2 },
                                }}
                              />
                            );
                          })}
                        </Box>
                      ))}
                    </Box>
                    {/* Vertical year selector — right side */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: '28px', flexShrink: 0, minWidth: 70 }}>
                      {years.map((y) => {
                        const active = y === heatmapYear;
                        return (
                          <Box key={y} onClick={() => setHeatmapYear(y)} sx={{
                            px: 1.2, py: 0.6, borderRadius: '8px', cursor: 'pointer', textAlign: 'center',
                            bgcolor: active ? c.bg : 'transparent',
                            border: `1px solid ${active ? c.textMuted + '40' : 'transparent'}`,
                            boxShadow: active ? `0 2px 8px ${c.textMuted}15` : 'none',
                            fontWeight: active ? 700 : 500,
                            color: active ? c.textDark : c.textMuted,
                            fontSize: '0.95rem',
                            transition: 'all 0.15s ease',
                            '&:hover': !active ? { bgcolor: `${c.textMuted}10` } : undefined,
                          }}>{y}</Box>
                        );
                      })}
                    </Box>
                  </Box>
                  {/* Custom tooltip */}
                  {heatmapHover && (
                    <Box sx={{
                      position: 'absolute',
                      left: heatmapHover.x, top: heatmapHover.y,
                      transform: 'translate(-50%, -100%) translateY(-10px)',
                      background: c.bg,
                      border: `1px solid ${c.textMuted}30`,
                      borderRadius: '10px',
                      p: 1.2,
                      boxShadow: `0 6px 20px ${c.textMuted}25`,
                      whiteSpace: 'nowrap',
                      pointerEvents: 'none',
                      zIndex: 10,
                    }}>
                      <Typography sx={{ color: c.textMuted, fontSize: '0.85rem', mb: 0.3 }}>{heatmapHover.cell.key}</Typography>
                      {heatmapHover.cell.count > 0 ? (
                        <Typography sx={{ color: c.textDark, fontSize: '0.95rem', fontWeight: 600 }}>
                          {heatmapHover.cell.count} game{heatmapHover.cell.count > 1 ? 's' : ''} · <span style={{ color: '#00A5C4' }}>{heatmapHover.cell.wins}W</span> · <span style={{ color: '#E4446E' }}>{heatmapHover.cell.losses}L</span> · {heatmapHover.cell.draws}D · <strong>{Math.round((heatmapHover.cell.wins / heatmapHover.cell.count) * 100)}% WR</strong>
                        </Typography>
                      ) : (
                        <Typography sx={{ color: c.textMuted, fontSize: '0.95rem' }}>No games</Typography>
                      )}
                    </Box>
                  )}
                  {/* Legend */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center', mt: 2 }}>
                    {heatmapMode === 'volume' ? (
                      <>
                        <Typography sx={{ color: c.textMuted, fontSize: '0.85rem', mr: 0.5 }}>Less</Typography>
                        {[0, 0.2, 0.4, 0.7, 1].map((r, i) => {
                          const fake = { count: Math.max(1, Math.round(r * heatmap.maxDayCount)) };
                          const { bg, border } = r === 0 ? { bg: `${c.textMuted}22`, border: `${c.textMuted}10` } : volumeColor(fake.count);
                          return <Box key={i} sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: bg, border: `1px solid ${border}` }} />;
                        })}
                        <Typography sx={{ color: c.textMuted, fontSize: '0.85rem', ml: 0.5 }}>More</Typography>
                      </>
                    ) : (
                      <>
                        <Typography sx={{ color: '#E4446E', fontSize: '0.85rem', mr: 0.5, fontWeight: 600 }}>0% WR</Typography>
                        {[0, 0.25, 0.5, 0.75, 1].map((wr, i) => {
                          const fake = { count: 10, wins: Math.round(wr * 10), losses: 0, draws: 0 };
                          const { bg, border } = winrateColor(fake);
                          return <Box key={i} sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: bg, border: `1px solid ${border}` }} />;
                        })}
                        <Typography sx={{ color: '#00A5C4', fontSize: '0.85rem', ml: 0.5, fontWeight: 600 }}>100% WR</Typography>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })()}

          {/* ── Recent Form (merged with Last 20 Games) ── */}
          {derived && (
            <Box sx={{ mb: 5 }}>
              <Typography sx={{ color: c.textDark, fontSize: { xs: '1.4rem', md: '1.6rem' }, fontWeight: 800, mb: 0.5 }}>Recent Form</Typography>
              <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mb: 3 }}>
                Your last {derived.recent.length} games — a better gut-check of where you are right now than your lifetime win rate. Hover any square for the game details.
              </Typography>
              {/* Row 1 — game grid (full width) */}
              <Box sx={{ ...cardSx, p: 3, mb: 2 }}>
                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1.5 }}>Last {derived.recent.length} Games (most recent first)</Typography>
                <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
                  {[...derived.recent].reverse().map((g, i) => {
                    const o = g.outcome;
                    const color = o === 'win' ? '#00A5C4' : o === 'loss' ? '#E4446E' : c.textMuted;
                    const letter = o === 'win' ? 'W' : o === 'loss' ? 'L' : 'D';
                    const date = String(g.game_date_utc).slice(0, 10);
                    const oppName = g.color === 'white' ? g.black_player_username : g.white_player_username;
                    const oppElo = g.color === 'white' ? g.black_player_elo : g.white_player_elo;
                    const opening = (g.opening_name || '').split(':')[0].split(',')[0].slice(0, 40);
                    const outcomeLabel = o === 'win' ? 'Win' : o === 'loss' ? 'Loss' : 'Draw';
                    const title = `${date}\n${outcomeLabel} as ${g.color}\nvs ${oppName || '—'} (${oppElo || '?'})\n${opening || ''}`.trim();
                    return (
                      <Box
                        key={i}
                        onClick={() => g.game_url && window.open(g.game_url, '_blank')}
                        title={title}
                        sx={{
                          width: 32, height: 32, borderRadius: '7px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: `${color}18`, border: `1px solid ${color}35`,
                          color, fontSize: '0.95rem', fontWeight: 800,
                          cursor: g.game_url ? 'pointer' : 'default',
                          transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
                          '&:hover': {
                            transform: 'translateY(-2px) scale(1.08)',
                            bgcolor: `${color}28`,
                            boxShadow: `0 4px 12px ${color}35`,
                          },
                        }}
                      >{letter}</Box>
                    );
                  })}
                </Box>
              </Box>
              {/* Row 2 — 4 tiles */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                <Box sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
                  <Typography sx={{ color: c.textMuted, fontSize: '1rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}>
                    {derived.streakType === 'win' ? 'Current Winning Streak' : derived.streakType === 'loss' ? 'Current Losing Streak' : 'Current Drawing Streak'}
                  </Typography>
                  <Typography sx={{
                    color: derived.streakType === 'win' ? '#00A5C4' : derived.streakType === 'loss' ? '#E4446E' : c.textMuted,
                    fontSize: '2.2rem', fontWeight: 900, lineHeight: 1.1,
                  }}>
                    {derived.streakLen}
                  </Typography>
                  <Typography sx={{ color: c.textMuted, fontSize: '0.95rem', mt: 0.3 }}>
                    {derived.streakType === 'win' ? 'games in a row' : derived.streakType === 'loss' ? 'losses in a row' : 'draws in a row'}
                  </Typography>
                </Box>
                <Box sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
                  <Typography sx={{ color: c.textMuted, fontSize: '1rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}>Best Winning Streak</Typography>
                  <Typography sx={{ color: '#00A5C4', fontSize: '2.2rem', fontWeight: 900, lineHeight: 1.1 }}>
                    {derived.longestWin}
                  </Typography>
                  <Typography sx={{ color: c.textMuted, fontSize: '0.95rem', mt: 0.3 }}>
                    games · worst losing run {derived.longestLoss}
                  </Typography>
                </Box>
                {(() => {
                  const cur = derived.last20;
                  const prev = derived.prev20;
                  const hasPrev = prev.games > 0;
                  const dWR = Math.round((cur.winrate - prev.winrate) * 100);
                  const dElo = Math.round(cur.eloChange - prev.eloChange);
                  const wrColor = !hasPrev ? c.textMuted : (dWR > 0 ? '#00A5C4' : dWR < 0 ? '#E4446E' : c.textMuted);
                  const eloColor = !hasPrev ? c.textMuted : (dElo > 0 ? '#00A5C4' : dElo < 0 ? '#E4446E' : c.textMuted);
                  return (
                    <>
                      <Box sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
                        <Typography sx={{ color: c.textMuted, fontSize: '1rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}>Win Rate</Typography>
                        <Typography sx={{ color: c.textDark, fontSize: '2.2rem', fontWeight: 900, lineHeight: 1.1 }}>{Math.round(cur.winrate * 100)}%</Typography>
                        {hasPrev && (
                          <Typography sx={{ color: wrColor, fontSize: '0.95rem', fontWeight: 700, mt: 0.3 }}>
                            {dWR > 0 ? '+' : ''}{dWR}% vs the 20 before
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
                        <Typography sx={{ color: c.textMuted, fontSize: '1rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}>Elo Change</Typography>
                        <Typography sx={{ color: c.textDark, fontSize: '2.2rem', fontWeight: 900, lineHeight: 1.1 }}>{cur.eloChange >= 0 ? '+' : ''}{Math.round(cur.eloChange)}</Typography>
                        {hasPrev && (
                          <Typography sx={{ color: eloColor, fontSize: '0.95rem', fontWeight: 700, mt: 0.3 }}>
                            {dElo > 0 ? '+' : ''}{dElo} vs the 20 before
                          </Typography>
                        )}
                      </Box>
                    </>
                  );
                })()}
              </Box>
            </Box>
          )}

          {/* ── Pace / ETA projection ── */}
          {activeTrajectory && activeTrajectory.games >= 20 && (() => {
            const cur = activeTrajectory.current;
            const games = activeTrajectory.games;
            const net = activeTrajectory.net_change;
            const rate = net / games;
            const accent = rate >= 0 ? '#00A5C4' : '#E4446E';
            let line;
            if (rate > 0) {
              const nextRound = Math.ceil((cur + 1) / 100) * 100;
              const gamesNeeded = Math.ceil((nextRound - cur) / rate);
              line = <>At this rate, you'll hit <strong>{nextRound}</strong> in about <strong>{gamesNeeded.toLocaleString()}</strong> games.</>;
            } else if (rate < 0) {
              const nextFloor = Math.floor((cur - 1) / 100) * 100;
              const gamesUntil = Math.ceil((cur - nextFloor) / Math.abs(rate));
              line = <>If this keeps up, you'd slide to <strong>{nextFloor}</strong> in about <strong>{gamesUntil.toLocaleString()}</strong> games. Time to refocus.</>;
            } else {
              line = <>You're flat — neither climbing nor sliding right now.</>;
            }
            return (
              <Box sx={{ mb: 5, background: `${accent}0E`, border: `1px solid ${accent}35`, borderRadius: '16px', p: 3 }}>
                <Typography sx={{ color: accent, fontSize: '1.05rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1 }}>Pace ({TYPE_LABELS[filterType] || filterType})</Typography>
                <Typography sx={{ color: c.textDark, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7 }}>
                  You're averaging <strong style={{ color: accent }}>{rate >= 0 ? '+' : ''}{rate.toFixed(2)}</strong> elo per game. {line} But you can increase your win rate by using the <strong>training room</strong>.
                </Typography>
              </Box>
            );
          })()}





        </Box>
      )}
    </Box>
  );
}
